import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, CircularProgress, Divider, Button, Switch, FormControlLabel, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import LogSummary from "./LogSummary";
import ScoreHeader from "./ScoreHeader";
import ParseProgress from "./ParseProgress";
import LogFindings from "./LogFindings";
import LogParamAudit from "./LogParamAudit";
import LogInspection from "./LogInspection";
import AnalysisTabs from "./analysis/AnalysisTabs";
import FilterPanel from "./analysis/FilterPanel";
import OscillationPanel from "./analysis/OscillationPanel";
import PIDPanel from "./analysis/PIDPanel";
import CompassPanel from "./analysis/CompassPanel";
import HardwarePanel from "./analysis/HardwarePanel";
import { PanelUnavailable } from "./analysis/PanelShell";
import ReanalyseNotice from "./analysis/ReanalyseNotice";
import LogTimeline from "./LogTimeline";
import LogCharts from "./LogCharts";
import VehiclePanel from "./vehicle/VehiclePanel";
import TagEditor from "./TagEditor";
import DronePicker from "./DronePicker";
import HardwareId from "./HardwareId";
import { ACCENT } from "./accent";
import { evaluateFindings } from "./findings";
import {
  getLog,
  pollLogUntilDone,
  listLogs,
  updateLogTags,
  setLogPublic,
  setLogDrone,
  getDownloadUrl,
  reparseLog,
} from "./api/newTelemetry";
import { startDownload } from "./downloadLogs";
import { useAssistant } from "../../assistant";
import { primeLogRecord } from "../../../hooks/useAssistantRecords";
import { APP_BASE } from "./paths";

const isInProgress = (status) => status === "pending" || status === "parsing";

/**
 * Tabs in the order an investigation actually runs: what happened, what was
 * flagged, then the module views behind those flags, then the record you fill
 * in yourself. `module` links a tab to the backend analysis module whose
 * severity dot and finding count it shows.
 */
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "findings", label: "Findings" },
  { id: "filters", label: "Filters", module: "filters" },
  { id: "oscillation", label: "Oscillation", module: "oscillation" },
  { id: "pid", label: "PID", module: "pid" },
  { id: "compass", label: "Compass", module: "compass" },
  { id: "hardware", label: "Hardware", module: "hardware" },
  { id: "params", label: "Parameters" },
  { id: "inspection", label: "Inspection" },
  { id: "charts", label: "Charts" },
];

/**
 * Renders one analysis module, or explains its absence in place.
 *
 * A null `analysis` means the log was parsed before the backend computed one -
 * distinct from a module that ran and found nothing, and the two read
 * differently to anyone trying to trust the page. The first case is fixable
 * from here, so it offers the fix.
 */
const AnalysisPanel = ({ log, module, render: Panel, onReanalysed }) => {
  if (!log.analysis) {
    return <ReanalyseNotice log={log} onStarted={onReanalysed} />;
  }
  const result = log.analysis.modules?.[module];
  if (!result) {
    return <PanelUnavailable reason="This analysis module did not run for this log." />;
  }
  return <Panel module={result} />;
};

/** Dedicated stats page for a single flight log (route /log-analyzer/:id). */
const LogDetailPage = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { openPanel, send } = useAssistant();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allTags, setAllTags] = useState([]);
  // Gathered by the panels below so the RCA export can include them.
  const [busy, setBusy] = useState(null);
  const [paramAudit, setParamAudit] = useState(null);
  const [inspectionRecord, setInspectionRecord] = useState(null);
  // The active tab lives in the URL hash so a link to one view survives a
  // reload and can be handed to whoever needs to look at that chart.
  const [tab, setTab] = useState(() => {
    const fromHash = window.location.hash.replace("#", "");
    return TABS.some((t) => t.id === fromHash) ? fromHash : "overview";
  });

  const selectTab = useCallback((next) => {
    setTab(next);
    // `<base href="/">` in index.html is what replaceState resolves a relative
    // URL against, so a bare "#tab" would drop the path and rewrite the address
    // bar to "/#tab" — which reloads onto the landing page.
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${next}`);
  }, []);
  const pollingRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLog(id);
      setLog(data);
      setError(null);
      primeLogRecord(data);
      if (isInProgress(data.status) && !pollingRef.current) {
        pollingRef.current = true;
        pollLogUntilDone(id)
          .then((final) => setLog(final))
          .catch(() => {})
          .finally(() => {
            pollingRef.current = false;
          });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    // Gather tags across the user's logs to suggest in the editor.
    listLogs({ pageSize: 100 })
      .then((d) => setAllTags([...new Set((d.logs || []).flatMap((l) => l.tags || []))].sort()))
      .catch(() => {});
  }, [load]);

  const saveTags = async (tags) => {
    const prev = log;
    setLog({ ...log, tags }); // optimistic
    try {
      const updated = await updateLogTags(id, tags);
      setLog(updated);
      setAllTags((cur) => [...new Set([...cur, ...tags])].sort());
    } catch {
      setLog(prev); // revert on failure
    }
  };

  // The log carries the association; the catalog is only the list to pick from.
  const saveDrone = async (ref) => {
    const updated = await setLogDrone(log.id, ref);
    setLog(updated || { ...log, drone_ref: ref });
  };

  const togglePublic = async (isPublic) => {
    const prev = log;
    setLog({ ...log, is_public: isPublic }); // optimistic
    try {
      setLog(await setLogPublic(id, isPublic));
    } catch {
      setLog(prev); // revert on failure
    }
  };

  const downloadRaw = async () => {
    setBusy("download");
    try {
      startDownload(await getDownloadUrl(id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  // Available on any parsed log, not only one missing its analysis: the
  // analysers change, and the stored payload is only ever as new as the last
  // parse. Re-running rebuilds the index, summary, artifact and analysis from
  // the raw file and writes all of them back.
  const reanalyse = async () => {
    setBusy("reanalyse");
    try {
      await reparseLog(id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const analyzeWithAssistant = () => {
    // Hand the assistant what the rules already found, so it reasons about
    // cause instead of re-deriving detection from raw messages.
    const findings = evaluateFindings(log);
    const detected = findings.length
      ? ` Automated checks flagged: ${findings.map((f) => `${f.title} (${f.severity}) — ${f.evidence}`).join(" ")}`
      : " Automated checks flagged nothing.";
    openPanel();
    send(
      `Analyze this flight log and flag anything concerning.${detected}`,
      [{ type: "LOG", id: log.id, label: log.file_name }]
    );
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={APP_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Logs
      </Button>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : !log ? (
        <Typography color="error" variant="body2">
          Could not load this log.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {log.file_name}
            </Typography>
            {log.status === "ready" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!!log.is_public}
                      onChange={(e) => togglePublic(e.target.checked)}
                      sx={{ "& .Mui-checked": { color: ACCENT }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT } }}
                    />
                  }
                  label="Public"
                  sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                />
                <Button
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={downloadRaw}
                  disabled={busy === "download"}
                  sx={{ color: theme.palette.text.secondary, textTransform: "none" }}
                >
                  {busy === "download" ? "Preparing…" : "Download log"}
                </Button>
                <Button
                  size="small"
                  startIcon={<ReplayOutlinedIcon />}
                  onClick={reanalyse}
                  disabled={busy === "reanalyse"}
                  sx={{ color: theme.palette.text.secondary, textTransform: "none" }}
                >
                  {busy === "reanalyse" ? "Starting…" : "Re-analyse"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoAwesomeOutlinedIcon />}
                  onClick={analyzeWithAssistant}
                  sx={{
                    color: ACCENT,
                    borderColor: ACCENT,
                    textTransform: "none",
                    "&:hover": { borderColor: ACCENT, backgroundColor: `${ACCENT}14` },
                  }}
                >
                  Analyze with assistant
                </Button>
              </Box>
            )}
          </Box>

          {log.status === "failed" ? (
            <Typography color="error" variant="body2">
              Parsing failed: {log.error || "unknown error"}
            </Typography>
          ) : isInProgress(log.status) ? (
            <ParseProgress log={log} />
          ) : (
            <>
              <AnalysisTabs tabs={TABS} value={tab} onChange={selectTab} analysis={log.analysis} />

              {tab === "overview" && (
                <>
                  <ScoreHeader log={log} />
                  <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    TAGS
                  </Typography>
                  <Box sx={{ mb: 3, maxWidth: 560 }}>
                    <TagEditor value={log.tags || []} onChange={saveTags} suggestions={allTags} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    AIRCRAFT
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <DronePicker value={log.drone_ref} onChange={saveDrone} />
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <HardwareId log={log} />
                  </Box>
                  <LogSummary log={log} />
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
                    VEHICLE
                  </Typography>
                  <VehiclePanel log={log} />
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
                    FLIGHT TIMELINE
                  </Typography>
                  <LogTimeline log={log} />
                </>
              )}

              {tab === "findings" && (
                <LogFindings log={log} paramAudit={paramAudit} inspectionRecord={inspectionRecord} />
              )}

              {tab === "filters" && <AnalysisPanel log={log} module="filters" render={FilterPanel} onReanalysed={load} />}
          {tab === "oscillation" && <AnalysisPanel log={log} module="oscillation" render={OscillationPanel} onReanalysed={load} />}
              {tab === "pid" && <AnalysisPanel log={log} module="pid" render={PIDPanel} onReanalysed={load} />}
              {tab === "compass" && <AnalysisPanel log={log} module="compass" render={CompassPanel} onReanalysed={load} />}
              {tab === "hardware" && <AnalysisPanel log={log} module="hardware" render={HardwarePanel} onReanalysed={load} />}

              {tab === "params" && <LogParamAudit log={log} onAudit={setParamAudit} />}

              {tab === "inspection" && (
                <LogInspection log={log} findings={evaluateFindings(log)} onChange={setInspectionRecord} />
              )}

              {tab === "charts" && <LogCharts log={log} />}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default LogDetailPage;
